import { ProgressGPTReplyTypeEnum } from 'api/enums';
import { IProgressGPTChatModel, ProgressGPTReplyModel } from 'api/models';
import clsx from 'clsx';
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion';
import { Backdrop } from 'library/backdrop';
import { Divider } from 'library/divider';
import { drawerAnimation } from 'library/drawer/helpers/drawer.animations';
import { CrossIcon, SahaIcon } from 'library/icons';
import { useDesktopMediaQuery } from 'library/media-query';
import useDialog from 'modules/shared/hooks/useDialog';
import { ForwardedRef, forwardRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import ScrollLock from 'react-scrolllock';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import { ProgressGPTSocketMessage } from '../../helpers/progressgpt.types';
import useProgressGPTAddReplyForm from '../../hooks/useProgressGPTAddReplyForm';
import useProgressGPTChatData from '../../hooks/useProgressGPTChatData';
import useProgressGPTChatListData from '../../hooks/useProgressGPTChatListData';
import useProgressGPTChatSuggestionData from '../../hooks/useProgressGPTChatSuggestionData';
import useProgressGPTCreateChatForm from '../../hooks/useProgressGPTCreateChatForm';
import useProgressGPTMarkdownTyping from '../../hooks/useProgressGPTMarkdownTyping';
import useProgressGPTSocketListener from '../../hooks/useProgressGPTSocketListener';
import ChatReplies from '../ChatReplies/ChatReplies';
import ChatRepliesLoading from '../ChatRepliesLoading/ChatRepliesLoading';
import DeleteChatDrawer from '../DeleteChatDrawer/DeleteChatDrawer';
import MessageForm from '../MessageForm/MessageForm';
import ProgressGPTBar from '../ProgressGPTBar/ProgressGPTBar';
import ProgressGPTContainer from '../ProgressGPTContainer/ProgressGPTContainer';
import ProgressGPTBarSuggestionLoading from '../ProgressGPTSuggestionLoading/ProgressGPTSuggestionLoading';

const notificationAudio = new Audio('/sounds/notification-01.mp3');

interface ProgressGPTDrawerProps extends HTMLMotionProps<'div'> {
    show: boolean;
    onClose: () => void;
    ref?: ForwardedRef<HTMLDivElement>;
}

const ProgressGPTDrawer = forwardRef<HTMLDivElement, ProgressGPTDrawerProps>((props, ref) => {
    const { show, onClose, className, ...rest } = props;

    const { projectId, cameraId } = useParams();
    const isDesktop = useDesktopMediaQuery();

    const [chatId, setChatId] = useState<string>(null);
    const [selectedChatId, setSelectedChatId] = useState<string>(null);
    const [isNewChat, setNewChat] = useState<boolean>(true);
    const [animateToBottom, setAnimateToBottom] = useState(false);

    const deleteChatDialog = useDialog<IProgressGPTChatModel>();

    const progressGPTChatListData = useProgressGPTChatListData();

    const progressGPTChatData = useProgressGPTChatData({ chatId: selectedChatId });
    const progressGPTChatSuggestionData = useProgressGPTChatSuggestionData();

    const addChatReplyForm = useProgressGPTAddReplyForm();

    const createChatForm = useProgressGPTCreateChatForm();

    const [typingId, setTypingId] = useProgressGPTMarkdownTyping();

    useEffect(() => {
        setNewChat(!chatId && (!progressGPTChatData.replies || progressGPTChatData.replies.length === 0));
    }, [chatId, progressGPTChatData.replies]);

    useProgressGPTSocketListener({
        onMessage: (data: ProgressGPTSocketMessage) => {
            // Add delay to the socket reply to avoid race condition api update
            setTimeout(() => {
                progressGPTChatData.addReply(ProgressGPTReplyModel.fromObject(data.userReply));
                progressGPTChatData.addReply(ProgressGPTReplyModel.fromObject(data.botReply));
            }, 1000);
        },
    });

    const handleAddReply = useCallback(
        async (message: string) => {
            if (isNewChat) {
                setAnimateToBottom(true);
                await new Promise((resolve) => setTimeout(resolve, 200));
            }
            setNewChat(false);

            let currentChatId = chatId;
            const tempReplyId = Math.random().toString(36).substring(2, 12);

            // Add optimistic user reply
            progressGPTChatData.addReply(
                ProgressGPTReplyModel.fromObject({
                    _id: tempReplyId,
                    message: message,
                    from: ProgressGPTReplyTypeEnum.USER,
                    createdAt: new Date(),
                })
            );

            try {
                // If no chat exists yet, create
                if (!currentChatId) {
                    const result = await createChatForm.submit({ message });
                    progressGPTChatListData.addChat(result);
                    currentChatId = result._id;
                    setChatId(currentChatId);
                }

                const metadata = chatId ? {} : { projectId, cameraId };
                const result = await addChatReplyForm.submit({
                    message,
                    chatId: currentChatId,
                    metadata,
                });

                // Replace optimistic reply with actual user reply
                progressGPTChatData.deleteReply(tempReplyId);
                progressGPTChatData.addReply(ProgressGPTReplyModel.fromObject(result.userReply));

                // Add bot reply
                const botReply = ProgressGPTReplyModel.fromObject(result.botReply);
                progressGPTChatData.addReply(botReply);
                setTypingId(botReply._id, { length: botReply.message.length });
            } catch (err: any) {
                // Remove optimistic reply upon error
                progressGPTChatData.deleteReply(tempReplyId);
                toast.error(err.data.message || 'Some error occurred, please try again.');
            }
        },
        [
            addChatReplyForm,
            cameraId,
            chatId,
            createChatForm,
            isNewChat,
            progressGPTChatData,
            progressGPTChatListData,
            projectId,
            setTypingId,
        ]
    );

    const handleNewChat = () => {
        setNewChat(true);
        setChatId(null);
        setAnimateToBottom(false);
        setSelectedChatId(null);
        progressGPTChatData.deleteAllReplies();
    };

    const handleDeleteChat = useCallback(
        (chatIdToDelete: string) => {
            progressGPTChatListData.deleteChat(chatIdToDelete);

            if (chatIdToDelete === chatId) {
                setChatId(null);
                setSelectedChatId(null);
                setNewChat(true);
                setAnimateToBottom(false);
                progressGPTChatData.deleteAllReplies();
            }
        },
        [chatId, progressGPTChatData, progressGPTChatListData]
    );

    return createPortal(
        <AnimatePresence initial={false} mode="wait" onExitComplete={onClose}>
            {show && (
                <>
                    <Backdrop show={show} onClick={onClose} className="z-[80] p-8" />

                    <ScrollLock>
                        <motion.div
                            {...rest}
                            {...drawerAnimation}
                            ref={ref}
                            className={twMerge(
                                'fixed right-0 top-0 bg-card-color max-w-full w-full md:w-[calc(100vw-var(--sidebar-width))] h-full min-h-full overflow-hidden z-[81]',
                                className
                            )}
                        >
                            <main className="flex h-full">
                                <div
                                    className={clsx(
                                        'md:relative md:flex-shrink-0',
                                        !isDesktop && 'absolute inset-y-0 left-0 z-50'
                                    )}
                                >
                                    <ProgressGPTBar
                                        chatId={chatId}
                                        chats={progressGPTChatListData.chats}
                                        isChatsLoading={progressGPTChatListData.isLoading}
                                        onClick={(chatId) => {
                                            setChatId(chatId);
                                            setSelectedChatId(chatId);
                                        }}
                                        onNewChat={handleNewChat}
                                        onDelete={deleteChatDialog.show}
                                    />
                                </div>

                                <div className="w-full overflow-hidden flex flex-col">
                                    <div className="relative overflow-y-auto  bg-floating-color outline-none w-full flex flex-col h-full">
                                        <div className="relative flex flex-col h-full">
                                            <div className="relative  z-10 flex-shrink-0">
                                                <div className="flex items-center justify-center p-4">
                                                    <SahaIcon height={24} />

                                                    <button
                                                        type="button"
                                                        className="absolute top-[18px] right-6 z-[82] btn-action"
                                                        onClick={onClose}
                                                    >
                                                        <CrossIcon className="w-3 h-3 text-grayscale-500" />
                                                    </button>
                                                </div>

                                                <Divider variant="light" />
                                            </div>

                                            {/* px-[32px] sm:px-[56px] md:px-[76px] lg:px-[124px] xl:px-[162px] */}
                                            {isNewChat ? (
                                                <ProgressGPTContainer className="flex-1 flex flex-col items-center justify-center relative z-10">
                                                    <div className="w-full space-y-6 animate-fade-in">
                                                        <div className="text-center mb-8 transform transition-all duration-500 ease-out animate-slide-up">
                                                            <h2 className="text-2xl font-semibold text-grayscale-900 mb-2">
                                                                How can I help you today?
                                                            </h2>
                                                        </div>

                                                        <div className="w-full ">
                                                            <motion.div
                                                                animate={
                                                                    animateToBottom
                                                                        ? {
                                                                              y: window.innerHeight * 0.4,
                                                                              opacity: animateToBottom ? 0.8 : 1,
                                                                          }
                                                                        : { y: 0, opacity: 1 }
                                                                }
                                                                transition={{
                                                                    duration: 0.4,
                                                                    ease: 'easeInOut',
                                                                    type: 'tween',
                                                                }}
                                                                className="w-full"
                                                            >
                                                                <MessageForm
                                                                    onMessage={handleAddReply}
                                                                    maxHeight={400}
                                                                    disabled={addChatReplyForm.isLoading}
                                                                />
                                                            </motion.div>
                                                        </div>
                                                        <motion.div
                                                            animate={animateToBottom ? { opacity: 0 } : { opacity: 1 }}
                                                            transition={{ duration: 0.2 }}
                                                        >
                                                            <div className="text-lg font-semibold text-grayscale-900 mb-2 text-center">
                                                                Explore what{' '}
                                                                <span className="ai-text-gradient">SahaAI</span> can do
                                                            </div>

                                                            <div className="flex flex-wrap items-center justify-center gap-3 mt-6 animate-fade-in-delayed">
                                                                {progressGPTChatSuggestionData.isLoading ? (
                                                                    <ProgressGPTBarSuggestionLoading />
                                                                ) : (
                                                                    progressGPTChatSuggestionData.suggestions?.map(
                                                                        (suggestion) => (
                                                                            <button
                                                                                key={suggestion.id}
                                                                                className="px-4 py-2 border border-grayscale-300 rounded-full text-sm text-grayscale-500 font-medium hover:bg-grayscale-100 transition"
                                                                                onClick={() =>
                                                                                    handleAddReply(suggestion.message)
                                                                                }
                                                                            >
                                                                                {suggestion.title}
                                                                            </button>
                                                                        )
                                                                    )
                                                                )}
                                                            </div>
                                                        </motion.div>
                                                    </div>
                                                </ProgressGPTContainer>
                                            ) : (
                                                <>
                                                    <div className="flex-1 min-h-0 relative z-10 overflow-y-auto hide-scrollbar max-h-full">
                                                        {progressGPTChatData.isLoading ? (
                                                            <ChatRepliesLoading />
                                                        ) : (
                                                            <ChatReplies
                                                                replies={progressGPTChatData.replies}
                                                                isLoading={addChatReplyForm.isLoading}
                                                                typingId={typingId}
                                                            />
                                                        )}
                                                    </div>

                                                    <ProgressGPTContainer className="py-6 bg-floating-color relative z-10">
                                                        <Divider color="light" className="mb-6" />
                                                        <MessageForm
                                                            onMessage={handleAddReply}
                                                            maxHeight={150}
                                                            disabled={addChatReplyForm.isLoading}
                                                        />
                                                    </ProgressGPTContainer>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <p className="px-4 text-center pb-6 text-xs font-medium max-w-2xl mx-auto">
                                        SahaAI may provide incorrect information. Users are advised to exercise
                                        discretion and independently verify results when necessary.
                                    </p>
                                </div>
                            </main>
                        </motion.div>
                    </ScrollLock>
                </>
            )}

            <DeleteChatDrawer
                chat={deleteChatDialog.data}
                show={deleteChatDialog.isOpen}
                onClose={deleteChatDialog.hide}
                onDelete={handleDeleteChat}
            />
        </AnimatePresence>,
        document.getElementById('drawer-root')
    );
});

export default ProgressGPTDrawer;
